import { create } from 'zustand';
import isOnline from 'is-online';
import Web3 from 'web3';
import { Core } from '@walletconnect/core';
import { getSdkError, SDK_ERRORS } from '@walletconnect/utils';
import { WalletConnectModal } from '@walletconnect/modal';
import { ProposalTypes, SessionTypes } from '@walletconnect/types';
import { ApiPromise, HttpProvider } from '@polkadot/api';
import SignClient from '@walletconnect/sign-client';
import { u8aToHex } from '@polkadot/util';
import { decodeAddress } from '@polkadot/util-crypto';
import { MARKETPLACE_CHAINS } from '@shared/types/enums';
import { useSignUpOperatorStore } from './sign-up-operator';
import { useWalletEnvStore } from './wallet-env';
import { useBalanceStore } from './balance';
import { useAddressBookStore } from './address-book';
import { useApiServiceStore } from '@ewf/stores/api';
import { routerConst } from '@ewf/lib/router';
import { router } from '../init-main';
import { useWalletStore } from './wallets';
import { isElectron } from '@shared/utils/is-electron';
import { getERC20Balance } from '../utils/balance-helpers';

type ConnectionStore = {
  init: () => void;
  isInitialized: boolean;
  cleanUp: () => void;
  status: 'preparing' | 'connecting' | 'connected' | 'disconnected';
  isConnected: boolean;
  isConnectedEWC: boolean;
  isConnectedETH: boolean;
  isEWX: boolean;
  addressEWX: string;
  publicAddressEWX: string;
  addressEWC: string;
  addressETH: string;
  errorMsg?: string;
  session: SessionTypes.Struct | undefined;
  sessionEWC: SessionTypes.Struct | undefined;
  sessionETH: SessionTypes.Struct | undefined;
  walletConnectModal: WalletConnectModal | undefined;
  client: SignClient | undefined;
  createClient: () => Promise<void>;
  connect: (namespace?: ProposalTypes.RequiredNamespaces, cb?: () => void) => Promise<void>;
  onSessionConnected: (deviceSession: {
    type: 'ledger' | 'trezor' | 'walletconnect';
    address?: string;
    balance?: string;
    path?: string;
    session?: SessionTypes.Struct;
    chain?: MARKETPLACE_CHAINS;
  }) => Promise<void>;
  disconnect: (chain?: MARKETPLACE_CHAINS) => Promise<void>;
  wcSessionReload: () => Promise<void>;
  reset: () => void;
  resetWC: () => void;
};

export const useConnectionStore = create<ConnectionStore>((set, get) => ({
  init: async () => {
    if (get().isInitialized) return;

    await useWalletEnvStore.getState().constants();
    await get().createClient();
    await get().wcSessionReload();
    set({ isInitialized: true });
  },
  cleanUp: () => {
    set({
      errorMsg: undefined,
      session: undefined,
      sessionEWC: undefined,
      sessionETH: undefined,
      client: undefined,
      walletConnectModal: undefined,
      isInitialized: false,
    });
  },
  isInitialized: false,
  status: 'disconnected',
  isConnected: false,
  isConnectedEWC: false,
  isConnectedETH: false,
  isEWX: true,
  addressEWC: '',
  addressEWX: '',
  addressETH: '',
  publicAddressEWX: '',
  session: undefined,
  sessionEWC: undefined,
  sessionETH: undefined,
  client: undefined,
  createClient: async () => {
    const { walletConst } = useWalletEnvStore.getState();

    if (!(await isOnline())) {
      throw new Error('network are not online');
    }

    const core = new Core({
      projectId: walletConst.projectId,
      relayUrl: walletConst.relayUrl,
      logger: 'error',
    });

    // Override default logger to capture errors
    core.logger.error = (obj, error) => {
      console.error(error);
      const msg = !error.message
        ? 'An unknown error occurred'
        : error.message.includes('JWT validation error')
          ? "WalletConnect connection error. Please check your device's time settings and internet connection."
          : error.message;

      if (msg === get().errorMsg) {
        return;
      }

      set({ errorMsg: msg });
    };

    const client = await SignClient.init({ core, metadata: walletConst.metadata });

    client.on('session_delete', ({ topic }: { id: number; topic: string }) => {
      if (get().session?.topic === topic) {
        get().resetWC();
      }
    });

    client.on('session_expire', ({ topic }: { topic: string }) => {
      if (get().session?.topic === topic) {
        get().resetWC();
      }
    });

    set({ client: client });
  },
  walletConnectModal: undefined,
  balance: undefined,
  connect: async (
    namespace: ProposalTypes.RequiredNamespaces = useWalletEnvStore.getState().walletConst[
      MARKETPLACE_CHAINS.EWX
    ],
    connectionPendingCallback?: () => void,
  ) => {
    const { client, walletConnectModal, session: ewxSession } = get();

    if (!(await isOnline())) {
      throw new Error('network are not online');
    }

    if (!walletConnectModal) {
      throw new Error('WalletConnectModal is not initialized');
    }

    if (typeof client === 'undefined') {
      throw new Error('WalletConnect is not initialized');
    }

    try {
      const connectClientPromise = client.connect({
        requiredNamespaces: namespace,
        pairingTopic: isElectron() ? undefined : ewxSession?.pairingTopic,
      });

      const timeoutPromise = new Promise(
        (_, reject) =>
          setTimeout(() => {
            if (get().errorMsg) return;

            reject(new Error('Connection timeout. Please, check your internet connection'));
          }, 5000), // 5 seconds
      );

      // If connection takes more than 5 seconds, throw timeout error
      const { uri, approval } = (await Promise.race([
        connectClientPromise,
        timeoutPromise,
      ])) as Awaited<ReturnType<typeof client.connect>>;

      if (uri) {
        walletConnectModal.openModal({ uri });
      }

      const session = await approval();

      // Callback function to let caller know that connection is pending
      // This is used when WalletConnect QR model is open, so it shouldn't be closed
      // because connection is still pending
      if (connectionPendingCallback) {
        connectionPendingCallback();
      }

      await get().onSessionConnected({ type: 'walletconnect', session });
    } catch (e) {
      console.error('Wallet connection error:', e);
      throw e;
    } finally {
      walletConnectModal.closeModal();
    }
  },
  onSessionConnected: async (deviceSession: {
    type: 'ledger' | 'trezor' | 'walletconnect';
    address?: string;
    balance?: string;
    path?: string;
    session?: SessionTypes.Struct;
    chain?: MARKETPLACE_CHAINS;
  }) => {
    const { walletConst } = useWalletEnvStore.getState();
    const { setWalletType } = useWalletStore.getState();
    const workerApi = useApiServiceStore.getState().api;

    if (
      deviceSession.session &&
      deviceSession.session.namespaces &&
      deviceSession.session.namespaces.eip155
    ) {
      try {
        const chainInfo = deviceSession.session.namespaces.eip155.accounts[0];
        const chainId = chainInfo.split(':')[1];
        const address = chainInfo.split(':')[2];
        const isETHChain = String(chainId) === String(walletConst.ethChainId);
        const isEWCChain = String(chainId) === String(walletConst.ewcChainId);

        if (isETHChain) {
          console.log('[onSessionConnected ]Connecting to Ethereum');
          if (!walletConst.ethRpc) {
            throw new Error('No Ethereum RPC found');
          }
          const httpProvider = new Web3.providers.HttpProvider(walletConst.ethRpc);
          const web3 = await new Web3(httpProvider);

          if (!walletConst.avtContractAddress) {
            throw new Error('No EWT contract address found');
          }

          // Fetch both ERC-20 EWT balance (main) and native ETH balance (gas)
          const [ewtBalance, ethBalance] = await Promise.all([
            getERC20Balance(web3, walletConst.avtContractAddress, address), // ERC-20 EWT
            web3.eth.getBalance(address), // Native ETH for gas
          ]);

          const balance = {
            token: web3.utils.fromWei(ewtBalance, 'ether'),
            base: ewtBalance,
            symbol: 'EWT', // Main balance is EWT
            gas: {
              token: web3.utils.fromWei(ethBalance, 'ether'),
              base: ethBalance,
              symbol: 'ETH', // Gas balance is native ETH
            },
          };
          set({ isConnectedETH: true, addressETH: address, sessionETH: deviceSession.session });
          useBalanceStore.setState({ balanceETH: balance });
          setWalletType('walletconnect');
        } else if (isEWCChain) {
          console.log('[onSessionConnected ]Connecting to EWC');
          const httpProvider = new Web3.providers.HttpProvider(walletConst.ewcRpc);
          const web3 = await new Web3(httpProvider);
          const available = await web3.eth.getBalance(address);
          const balance = {
            token: web3.utils.fromWei(available, 'ether'),
            base: available,
            symbol: 'VT',
          };
          set({ isConnectedEWC: true, addressEWC: address, sessionEWC: deviceSession.session });
          useBalanceStore.setState({ balanceEWC: balance });
          setWalletType('walletconnect');
        } else {
          console.warn('Unknown chain ID:', chainId);
          throw new Error(
            `Unsupported chain ID: ${chainId}. Please switch to Ethereum (${walletConst.ethChainId}) or Volta (${walletConst.ewcChainId})`,
          );
        }
      } catch (error) {
        console.error(error);
        throw error;
      }
    } else if (deviceSession && deviceSession.type === 'ledger') {
      const chain = deviceSession.chain || MARKETPLACE_CHAINS.EWX; // Default to EWX for backward compatibility

      if (chain === MARKETPLACE_CHAINS.EWX) {
        const publicAddressEWX = u8aToHex(decodeAddress(deviceSession.address));
        set({
          isConnected: true,
          addressEWX: deviceSession.address,
          session: undefined,
          publicAddressEWX: publicAddressEWX,
        });
        await workerApi.sendEwxAddress(
          deviceSession.address ? deviceSession.address : '',
          '',
          'ledger',
          deviceSession.path,
        );

        workerApi
          .isKYCDone(deviceSession.address ? deviceSession.address : '')
          .then((isOperatorSignedUp) => {
            useSignUpOperatorStore.setState({ isOperatorSignedUp });
          });

        useBalanceStore.getState().setBalanceEWX(deviceSession.balance);
      } else if (chain === MARKETPLACE_CHAINS.EWC || chain === MARKETPLACE_CHAINS.ETH) {
        // Handle Ledger EWC/ETH connections
        const isEthChain = chain === MARKETPLACE_CHAINS.ETH;
        if (isEthChain) {
          // For Ledger ETH, the balance structure should be set by ConnectLedger component
          // which has access to both EWT token balance and ETH gas balance
          set({ isConnectedETH: true, addressETH: deviceSession.address });
        } else {
          set({ isConnectedEWC: true, addressEWC: deviceSession.address });
          useBalanceStore.getState().setBalanceEWC({
            token: deviceSession.balance || '0',
            base: Web3.utils.toWei(deviceSession.balance || '0', 'ether'),
            symbol: 'EWT',
          });
        }
      }

      useWalletStore.setState({ devicePath: deviceSession.path });
      setWalletType('ledger');
    } else {
      const api = await ApiPromise.create({
        provider: new HttpProvider(walletConst.ewxPalletRpc),
      });
      try {
        const address = deviceSession.session!.namespaces.polkadot.accounts[0].split(':')[2];
        const publicAddressEWX = u8aToHex(decodeAddress(address));
        set({
          isConnected: true,
          addressEWX: address,
          session: deviceSession.session,
          publicAddressEWX: publicAddressEWX,
        });

        await workerApi.sendEwxAddress(address, deviceSession.session!.topic);
        const isOperatorSignedUp = await workerApi.isKYCDone(address);
        useSignUpOperatorStore.setState({ isOperatorSignedUp });

        useBalanceStore.setState({ isLoading: true });
        workerApi
          .queryBalance()
          .catch((e) => console.error(e))
          .finally(() => {
            useBalanceStore.setState({ isLoading: false });
          });
        setWalletType('walletconnect');

        if (!isElectron()) {
          router.navigate(routerConst.Dashboard);
        }
      } catch (error) {
        console.error(error);
      } finally {
        await api.disconnect();
      }
    }
    useAddressBookStore.getState().fetchAccounts();
  },
  disconnect: async (chain: MARKETPLACE_CHAINS = MARKETPLACE_CHAINS.EWX) => {
    const workerApi = useApiServiceStore.getState().api;
    const { addressEWX, session, sessionEWC, sessionETH, client, reset } = get();

    if (typeof client === 'undefined' || !addressEWX) {
      throw new Error('WalletConnect is not initialized');
    }

    if (chain === MARKETPLACE_CHAINS.EWC) {
      if (typeof sessionEWC === 'undefined') {
        set({ isConnectedEWC: false, sessionEWC: undefined });
        useBalanceStore.setState({ balanceEWC: undefined });
        return;
      }

      set({ isConnectedEWC: false, sessionEWC: undefined });
      useBalanceStore.setState({ balanceEWC: undefined });
    }

    if (chain === MARKETPLACE_CHAINS.ETH) {
      if (typeof sessionETH === 'undefined') {
        set({ isConnectedETH: false, sessionETH: undefined });
        useBalanceStore.setState({ balanceETH: undefined });
        return;
      }

      set({ isConnectedETH: false, sessionETH: undefined });
      useBalanceStore.setState({ balanceETH: undefined });
    }

    if (chain === MARKETPLACE_CHAINS.EWX) {
      if (typeof session === 'undefined') {
        reset();
        return;
      }

      try {
        await client.disconnect({
          topic: session.topic,
          reason: getSdkError('USER_DISCONNECTED'),
        });
      } catch (error) {
        console.error('SignClient.disconnect failed:', error);
      } finally {
        await workerApi.resetAllSubscriptionsByEwx(addressEWX);
        reset();
      }
    }
  },
  wcSessionReload: async () => {
    const workerApi = useApiServiceStore.getState().api;
    const { client, onSessionConnected } = get();
    const { walletConst } = useWalletEnvStore.getState();

    if (!client) {
      throw new Error('Client not initialized');
    }

    const ewxAccount = await workerApi.getLastEWXAccount();
    if (walletConst.env === ewxAccount.env && ewxAccount.ewxAddress && ewxAccount.wcSessionId) {
      /**
       * We need to find all of our session
       */
      const sessions = client.find({
        requiredNamespaces: walletConst.CHAINS_NAMESPACES[MARKETPLACE_CHAINS.EWX],
      });
      const pairings = client.core.pairing.getPairings();

      /**
       * We need to match active session with active pairings
       * only those are able to make connection with WC
       */
      const activeSessions = sessions.filter((session) =>
        pairings.some(({ topic }) => session.pairingTopic === topic),
      );

      /**
       * We need to also remove all non active sessions -
       * sessions that not have any active pairing connected
       */
      const nonActiveSessions = sessions.filter((session) =>
        pairings.every(({ topic }) => session.pairingTopic !== topic),
      );
      await Promise.all(
        nonActiveSessions.map(({ topic }) =>
          client.session.delete(topic, SDK_ERRORS.USER_DISCONNECTED),
        ),
      );
      const TIMESTAMP_NOW_IN_SECONDS = Math.floor(new Date().getTime() / 1000.0);

      const [session] = activeSessions;

      if (session && session.expiry > TIMESTAMP_NOW_IN_SECONDS) {
        await client.core.pairing.activate({ topic: session.pairingTopic });
        await onSessionConnected({ type: 'walletconnect', session });
      } else {
        if (activeSessions.length > 0) {
          await workerApi.sendEwxAddress(ewxAccount.ewxAddress);
        }
      }
    } else if (
      walletConst.env === ewxAccount.env &&
      ewxAccount.ewxAddress &&
      ewxAccount.walletType === 'ledger'
    ) {
      const balance = await workerApi.queryBalance();
      await onSessionConnected({
        type: 'ledger',
        address: ewxAccount.ewxAddress,
        balance,
        path: ewxAccount.walletPath,
      });
    }
  },
  reset: () => {
    const workerApi = useApiServiceStore.getState().api;
    set({
      session: undefined,
      sessionETH: undefined,
      isEWX: false,
      isConnected: false,
      isConnectedETH: false,
      addressEWX: '',
      addressETH: '',
      publicAddressEWX: '',
    });
    useBalanceStore.setState({
      balanceEWX: undefined,
      balanceEWC: undefined,
      balanceETH: undefined,
    });
    useSignUpOperatorStore.setState({ isOperatorSignedUp: false });
    useWalletStore.setState({ wallet: undefined });
    workerApi.sendEwxAddress('');
  },
  resetWC: () => {
    set({
      session: undefined,
      sessionETH: undefined,
      isEWX: false,
      isConnected: false,
      isConnectedETH: false,
      addressEWX: '',
      addressETH: '',
      publicAddressEWX: '',
    });
    useBalanceStore.setState({
      balanceEWX: undefined,
      balanceEWC: undefined,
      balanceETH: undefined,
    });
  },
}));

useConnectionStore.subscribe((state, previousState) => {
  if (state.status === previousState.status) {
    return;
  }

  const isConnected = state.status === 'connected';

  useConnectionStore.setState({ isConnected });
});
